import { useCallback, useMemo, useState } from "react";
import { userActions } from "actions";
import { useSessionData, fetchClientIpAndCountry } from "helpers";
import { useAcceptTermsMutation, useGetTermsStatusCheckQuery } from "store/api/termsApi";
import { TERMS_STATUS_QUERY_OPTIONS } from "store/api/stableQueryOptions";
import { useAppDispatch } from "types/hooks";
import { extractPendingTermsFromResponse } from "../termsStatusUtils";

export function useLayoutTermsGate() {
  const dispatch = useAppDispatch();
  const { business_id, device_token, session_id } = useSessionData();
  const canCheck = !!business_id && !!device_token && !!session_id;
  const [actionError, setActionError] = useState<string | null>(null);

  const sessionArgs = useMemo(
    () => ({
      business_id: business_id ?? "",
      device_token: device_token ?? "",
      session_id: session_id ?? "",
    }),
    [business_id, device_token, session_id]
  );

  const { data, isLoading, isUninitialized } = useGetTermsStatusCheckQuery(sessionArgs, {
    skip: !canCheck,
    ...TERMS_STATUS_QUERY_OPTIONS,
  });

  const [acceptTerms, { isLoading: isAccepting }] = useAcceptTermsMutation();

  const pendingTerms = useMemo(() => extractPendingTermsFromResponse(data), [data]);

  const isChecking = !canCheck || isUninitialized || isLoading;
  const isOpen = canCheck && !isChecking && pendingTerms !== null;

  const handleAccept = useCallback(async () => {
    if (!pendingTerms || !business_id || !device_token || !session_id) return;

    setActionError(null);
    try {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 12_000);
      let country = "";
      let ip_address = "";
      try {
        const meta = await fetchClientIpAndCountry(controller.signal);
        country = meta.country;
        ip_address = meta.ip_address;
      } finally {
        window.clearTimeout(timeoutId);
      }

      const countryOut = country.trim() || "Unknown";
      const ipOut = ip_address.trim() || "0.0.0.0";

      const response = await acceptTerms({
        business_id,
        device_token,
        session_id,
        terms_id: pendingTerms.terms_id,
        country: countryOut,
        ip_address: ipOut,
      }).unwrap();

      if (response?.success === false) {
        const message = Array.isArray(response.errors)
          ? response.errors.join(", ")
          : response.errors;
        setActionError(message || "Unable to accept the terms and conditions.");
      }
    } catch {
      setActionError("Unable to accept the terms and conditions. Please try again.");
    }
  }, [acceptTerms, business_id, device_token, pendingTerms, session_id]);

  const handleReject = useCallback(() => {
    dispatch(userActions.logout());
  }, [dispatch]);

  return {
    isOpen,
    isChecking,
    isSubmitting: isAccepting,
    pendingTerms,
    actionError,
    onAccept: handleAccept,
    onReject: handleReject,
  };
}
